import React, { lazy, useEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from './store/userSlice';
import { getUserProfile } from './utils/actions/authActions';
import { AuthContextProvider } from './context/AuthContext';
import AuthLayout from './components/AuthLayout';
import UpdateNotification from './components/UpdateNotification';

const Layout = lazy(() => import('./containers/Layout'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const VerifyOTP = lazy(() => import('./pages/VerifyOTP'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const VerifyNumber = lazy(() => import('./pages/VerifyNumber'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerificationSuccess = lazy(() => import('./pages/VerificationSuccess'));
const SecurityQuestions = lazy(() => import('./pages/SecurityQuestions'));
const ThemedSuspense = lazy(() => import('./components/ThemedSuspense'));
const PolicyTermsWPP = lazy(() => import('./policies/PolicyTermsWPP'));
const UnauthPremiumCheckout = lazy(() => import('./pages/UnauthPremiumCheckout'));
const UnauthClaimsFlow = lazy(() => import('./pages/UnauthClaimsFlow'));
const UnauthBenefitFlow = lazy(() => import('./pages/UnauthBenefitFlow'));
const PDFGeneratorPage = lazy(() => import('./pages/PDFGeneratorPage'));
const PDFFormFiller = lazy(() => import('./pages/PDFFormFiller'));
const PDFFormFillerMortgage = lazy(() => import('./pages/PDFFormFillerMortgage'));
const AgentDashboard2 = lazy(() => import('./pages/AgentDashboard2'));
const AdminPortal = lazy(() => import('./containers/AdminPortal'));

function App() {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);

  // The persisted user keeps whatever name it was given at sign-in, so refresh it from the
  // backend (agent_name = full_name from the external API) for sessions that are already open.
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    (async () => {
      const result = await getUserProfile(accessToken);
      const profile = result.success ? result.profile : null;
      // Clients carry first/last name of their own; only agents rely on `name`.
      if (cancelled || !profile?.name || profile.first_name) return;
      const [first_name, ...rest] = profile.name.trim().split(/\s+/);
      dispatch(setUser({ first_name, last_name: rest.join(' ') }));

      // Several screens read the sign-in snapshot in localStorage, so correct that copy too.
      try {
        const stored = JSON.parse(localStorage.getItem('agentData') || 'null');
        if (stored && stored.agent_name !== profile.name) {
          localStorage.setItem('agentData', JSON.stringify({ ...stored, agent_name: profile.name }));
        }
      } catch (error) {
        // A malformed snapshot is rebuilt at the next sign-in; nothing to do here.
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken, dispatch]);

  // Set up periodic update checks
  useEffect(() => {
    const checkInterval = setInterval(() => {
      // Trigger manual check every 15 minutes
      const event = new CustomEvent('checkForUpdates');
      window.dispatchEvent(event);
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(checkInterval);
  }, []);

  // Add a manual update checker to window for debugging
  useEffect(() => {
    window.checkForUpdates = () => {
      const event = new CustomEvent('checkForUpdates');
      window.dispatchEvent(event);
    };
  }, []);

  return (
    <>
      {/* Update Notification Component */}
      <UpdateNotification />
      
      <AuthContextProvider>
        <Switch>
            {/* Authentication Routes */}
            <Route path="/auth/signin" render={() => (
              <AuthLayout>
                <Login />
              </AuthLayout>
            )} />

            <Route path="/auth/signup" render={() => (
              <AuthLayout>
                <Register />
              </AuthLayout>
            )} />

            <Route path="/auth/verify-number" render={() => (
              <AuthLayout>
                <VerifyNumber />
              </AuthLayout>
            )} />

            <Route path="/auth/verify-email" render={() => (
              <AuthLayout>
                <VerifyEmail />
              </AuthLayout>
            )} />

            <Route path="/auth/verification-success" render={() => (
              <AuthLayout>
                <VerificationSuccess />
              </AuthLayout>
            )} />

            <Route path="/auth/suspense" render={() => (
              <ThemedSuspense />
            )} />

            <Route path="/auth/security-questions" render={() => (
              <AuthLayout>
                <SecurityQuestions />
              </AuthLayout>
            )} />

            <Route path="/auth/forgot-password" render={() => (
              <AuthLayout>
                <ForgotPassword />
              </AuthLayout>
            )} />

            <Route path="/auth/verify-otp" render={() => (
              <AuthLayout>
                <VerifyOTP />
              </AuthLayout>
            )} />

            <Route path="/auth/reset-password" render={() => (
              <AuthLayout>
                <ResetPassword />
              </AuthLayout>
            )} />

            <Route path="/auth/activate-account" render={() => (
              <AuthLayout>
                <ActivateAccount />
              </AuthLayout>
            )} />

            {/* Admin Portal */}
            <Route path="/admin" component={AdminPortal} />

            {/* Main Layout Route for All /app Paths */}
            <Route path="/app" component={Layout} />

            {/* Shared and Unprotected Routes */}
            <Route path="/wpp-terms-and-conditions" component={PolicyTermsWPP} />
            <Route path="/pay-premium" component={UnauthPremiumCheckout} />
            <Route path="/pdfs" component={PDFGeneratorPage} />
            <Route path="/claim-request" component={UnauthClaimsFlow} />
            <Route path="/benefit-request" component={UnauthBenefitFlow} />
            <Route path="/key-man-policy" component={PDFFormFiller} />
            <Route path="/mortgage-protection-policy" component={PDFFormFillerMortgage} />
            <Route path="/agent2" component={AgentDashboard2} />

            {/* Redirect to Sign In if no match */}
            <Redirect exact from="/" to="/auth/signin" />
          </Switch>
      </AuthContextProvider>
    </>
  );
}

export default App;